import { useState, type FormEvent } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader2, KeyRound, Eye, EyeOff } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../theme/ThemeContext'
import { ApiError } from '../lib/api'

type Step = 'credentials' | 'mfa'

export default function Login() {
  const { t } = useTranslation('auth')
  const { login, sessionExpired } = useAuth()
  const { theme } = useTheme()
  const isLight = theme === 'light'
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: string } | null)?.from ?? '/dashboard'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [step, setStep] = useState<Step>('credentials')
  const [mfaCode, setMfaCode] = useState('')
  const [useRecovery, setUseRecovery] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const opts =
        step === 'mfa'
          ? useRecovery
            ? { recoveryCode: mfaCode.trim() }
            : { mfaCode: mfaCode.trim() }
          : undefined
      const result = await login(email, password, opts)
      if (result.kind === 'ok') {
        navigate(from, { replace: true })
        return
      }
      if (result.kind === 'enrollment-required') {
        // Forzamos navegación a Seguridad y mostramos aviso.
        navigate('/settings/security', { replace: true })
        return
      }
      // mfa-required
      setStep('mfa')
      setMfaCode('')
      setError(null)
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? t('errors.loginFailed'))
      } else {
        setError(t('errors.serverUnreachable'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 font-heebo t-primary relative overflow-hidden">
      <div className="fixed inset-0 -z-10 bg-page" />
      <div
        className="fixed inset-0 -z-10 pointer-events-none"
        style={{
          background:
            'radial-gradient(closest-side at 30% 20%, rgba(27,79,216,0.35), transparent 60%), radial-gradient(closest-side at 70% 80%, rgba(79,124,249,0.22), transparent 65%)',
        }}
      />

      <div className="w-full max-w-md surface p-8 animate-slideUp">
        <div className="flex flex-col items-center mb-6">
          <div className="flex items-center gap-2 mb-2">
            <img
              src={isLight ? '/logo-mark.svg' : '/logo-mark-light.svg'}
              alt="JR2"
              draggable={false}
              className="h-9 w-auto select-none"
            />
            <span className="os-badge">OS</span>
          </div>
          <p className="t-muted text-sm">{t('tagline')}</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          {sessionExpired && step === 'credentials' && !error && (
            <div className="text-sm text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-lg px-3 py-2">
              {t('sessionExpired')}
            </div>
          )}
          {step === 'credentials' ? (
            <>
              <div>
                <label className="block text-sm t-secondary mb-1.5" htmlFor="email">
                  {t('form.email')}
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary placeholder-[var(--text-faint)] focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60 transition"
                />
              </div>
              <div>
                <label className="block text-sm t-secondary mb-1.5" htmlFor="password">
                  {t('form.password')}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2.5 pr-11 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary placeholder-[var(--text-faint)] focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60 transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={
                      showPassword ? t('form.hidePassword') : t('form.showPassword')
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 t-muted hover:t-secondary"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="text-center pb-2">
                <KeyRound size={22} className="mx-auto text-toga-300 mb-1" />
                <p className="text-sm t-primary font-semibold">{t('mfa.title')}</p>
                <p className="text-xs t-muted mt-1">
                  {t('mfa.subtitle')}
                </p>
              </div>
              <div>
                <label className="block text-sm t-secondary mb-1.5" htmlFor="mfa-code">
                  {useRecovery ? t('mfa.recoveryLabel') : t('mfa.totpLabel')}
                </label>
                <input
                  id="mfa-code"
                  inputMode={useRecovery ? 'text' : 'numeric'}
                  autoComplete="one-time-code"
                  required
                  autoFocus
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  maxLength={useRecovery ? 20 : 8}
                  placeholder={useRecovery ? 'xxxxx-xxxxx' : '123456'}
                  className="w-full px-4 py-2.5 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary placeholder-[var(--text-faint)] focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60 transition font-mono text-center tracking-widest"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setUseRecovery((v) => !v)
                  setMfaCode('')
                }}
                className="text-xs t-muted hover:text-toga-300 underline-offset-2 hover:underline"
              >
                {useRecovery
                  ? t('mfa.useTotp')
                  : t('mfa.useRecovery')}
              </button>
            </>
          )}

          {error && (
            <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/40 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={
              submitting ||
              (step === 'credentials' ? !email || !password : !mfaCode)
            }
            className="w-full py-2.5 rounded-lg font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 size={16} className="animate-spin" />}
            {submitting
              ? step === 'mfa'
                ? t('actions.verifying')
                : t('actions.signingIn')
              : step === 'mfa'
                ? t('actions.verify')
                : t('actions.signIn')}
          </button>

          {step === 'mfa' && (
            <button
              type="button"
              onClick={() => {
                setStep('credentials')
                setMfaCode('')
                setError(null)
              }}
              className="w-full text-xs t-muted hover:t-secondary"
            >
              {t('actions.back')}
            </button>
          )}
        </form>

        <p className="t-faint text-xs text-center mt-6">
          {t('forgotPassword')}
        </p>
      </div>
    </div>
  )
}
